import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAppStore } from "../store/useAppStore";
import { generateFollowUpMessage, generateDealSummary } from "../lib/gemini";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { FreeSkipTraceDialog } from "../components/FreeSkipTraceDialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

import { EditLeadDialog } from "../components/EditLeadDialog";
import { DocumentManager } from "../components/DocumentManager";
import { ArrowLeft, Sparkles, CalendarPlus, CheckCircle2, Trash2, Phone, MessageSquare, Voicemail, Building, MapPin, Zap, Clock3, Activity, ArrowUpRight, AlertCircle } from "lucide-react";
import { LeadStatus } from "../types";
import { format, parseISO, addDays, isPast, isToday, formatDistanceToNow } from "date-fns";

export default function LeadDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { leads, opportunities, updateLead, deleteLead, addFollowUp, completeFollowUp, addActivity } = useAppStore();
  
  const lead = leads.find((l) => l.id === id);
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedMessage, setGeneratedMessage] = useState("");
  const [noteContent, setNoteContent] = useState("");
  
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [dealSummary, setDealSummary] = useState("");
  
  // Follow up state
  const { cadence } = useAppStore();
  const [followUpDays, setFollowUpDays] = useState(cadence);
  const [followUpNotes, setFollowUpNotes] = useState("");

  if (!lead) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh]">
        <h2 className="text-2xl font-bold">Lead Not Found</h2>
        <Button variant="link" onClick={() => navigate("/leads")}>Back to Leads</Button>
      </div>
    );
  }

  const sortedActivities = [...(lead.activities || [])].sort(
    (a, b) => parseISO(b.timestamp).getTime() - parseISO(a.timestamp).getTime()
  );
  const activityCount = sortedActivities.length;
  const pendingFollowUps = lead.followUps.filter((f) => !f.completed).sort((a, b) => parseISO(a.dueDate).getTime() - parseISO(b.dueDate).getTime());
  const completedFollowUps = lead.followUps.filter((f) => f.completed).sort((a, b) => parseISO(b.dueDate).getTime() - parseISO(a.dueDate).getTime());
  const overdueFollowUps = pendingFollowUps.filter((f) => isPast(parseISO(f.dueDate)) && !isToday(parseISO(f.dueDate)));
  const nextFollowUp = pendingFollowUps[0];
  const lastTouchedAt = sortedActivities[0]?.timestamp || lead.updatedAt;
  const opportunityScore = lead.opportunityScore;
  const priorityLabel = overdueFollowUps.length > 0 || lead.status === "Follow-Up"
    ? "High priority"
    : opportunityScore !== undefined && opportunityScore >= 80
      ? "Hot lead"
      : opportunityScore !== undefined && opportunityScore >= 60
        ? "Warm lead"
        : "Standard priority";
  const priorityTone = overdueFollowUps.length > 0 || lead.status === "Follow-Up"
    ? "destructive"
    : opportunityScore !== undefined && opportunityScore >= 80
      ? "default"
      : "secondary";
  const nextActionCopy = nextFollowUp
    ? isToday(parseISO(nextFollowUp.dueDate))
      ? "Due today — make the next touch count."
      : `Next reminder: ${format(parseISO(nextFollowUp.dueDate), "MMM d")}`
    : "No reminder set yet — schedule the next touch on the right.";

  const handleUpdate = (field: keyof typeof lead, value: any) => {
    updateLead(lead.id, { [field]: value });
  };

  const handleQuickAction = (actionText: string, type: import("../types").ActivityType) => {
    addActivity(lead.id, type, actionText);
    toast.success(`Logged: ${actionText}`);
  };

  const handleAddNote = () => {
    if (!noteContent.trim()) return;
    addActivity(lead.id, "note", noteContent);
    setNoteContent("");
    toast.success("Note added");
  };

  const handleGenerateMessage = async () => {
    setIsGenerating(true);
    setGeneratedMessage("");
    try {
      const msg = await generateFollowUpMessage(
        lead.sellerName, 
        lead.motivation, 
        lead.propertyAddress, 
        lead.askingPrice,
        useAppStore.getState().businessName,
        useAppStore.getState().followUpStyle,
        lead.notes
      );
      setGeneratedMessage(msg);
      addActivity(lead.id, "text", "AI Message Generated");
      toast.success("Message generated successfully");
    } catch (error) {
      toast.error("Failed to generate message");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSummarizeDeal = async () => {
    setIsSummarizing(true);
    setDealSummary("");
    try {
      const summary = await generateDealSummary(
        lead.sellerName,
        lead.motivation,
        lead.propertyAddress,
        lead.askingPrice,
        lead.tags
      );
      setDealSummary(summary);
      addActivity(lead.id, "note", "AI Deal Summary Generated");
      toast.success("Summary generated successfully");
    } catch (error) {
       toast.error("Failed to generate summary");
    } finally {
      setIsSummarizing(false);
    }
  };

  const handleAddFollowUp = () => {
    const days = parseInt(followUpDays, 10);
    const dueDate = addDays(new Date(), days).toISOString();
    addFollowUp(lead.id, dueDate, followUpNotes);
    setFollowUpNotes("");
    toast.success(`Follow-up scheduled for ${days} day(s) from now`);
  };


  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 pb-10">
      <div className="overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-sm shadow-slate-900/5">
        <div className="grid gap-6 px-5 py-6 sm:px-8 lg:grid-cols-[1.1fr_.9fr] lg:items-start lg:px-10 lg:py-8">
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="shrink-0 rounded-full border border-slate-200 bg-slate-50 text-slate-600 shadow-sm hover:bg-slate-100">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">
                <span>Relationship view</span>
                <span className="h-1 w-1 rounded-full bg-slate-300" />
                <span>Lead detail</span>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl truncate">{lead.sellerName}</h1>
              <Badge variant={lead.status === "Dead Lead" ? "destructive" : "default"} className="shadow-sm">
                {lead.status}
              </Badge>
              {priorityLabel === "High priority" ? (
                <Badge variant="destructive" className="shadow-sm">High priority</Badge>
              ) : (
                <Badge variant={priorityTone} className="shadow-sm">{priorityLabel}</Badge>
              )}
              {opportunityScore !== undefined && (
                <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700 shadow-sm">
                  Score {opportunityScore}
                </Badge>
              )}
            </div>
            <p className="max-w-3xl text-sm leading-6 text-slate-500 break-words">{lead.propertyAddress}</p>
            <div className="flex flex-wrap gap-2">
              {lead.tags && lead.tags.length > 0 && lead.tags.map((tag, index) => (
                <Badge key={index} variant="outline" className="text-[10px] uppercase font-bold tracking-wider text-slate-500 bg-slate-100 border-slate-200">
                  {tag}
                </Badge>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs sm:text-sm text-slate-500">
              <span className="inline-flex items-center gap-1.5">
                <Clock3 className="h-3.5 w-3.5" /> Updated {formatDistanceToNow(parseISO(lead.updatedAt), { addSuffix: true })}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Activity className="h-3.5 w-3.5" /> {activityCount} activities
              </span>
              {nextFollowUp ? (
                <span className={`inline-flex items-center gap-1.5 ${isPast(parseISO(nextFollowUp.dueDate)) && !isToday(parseISO(nextFollowUp.dueDate)) ? "text-destructive" : ""}`}>
                  <AlertCircle className="h-3.5 w-3.5" /> Next follow-up {isToday(parseISO(nextFollowUp.dueDate)) ? "today" : format(parseISO(nextFollowUp.dueDate), "MMM d")}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5"><AlertCircle className="h-3.5 w-3.5" /> No follow-up scheduled</span>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-4 lg:items-end">
            <div className="flex flex-wrap gap-2 lg:justify-end">
              <a href={`tel:${lead.phone.replace(/[^0-9+]/g, "")}`} className="inline-flex">
                <Button variant="outline" size="sm" className="gap-2 border-slate-200 bg-slate-50 shadow-sm hover:bg-slate-100">
                  <Phone className="h-4 w-4" /> Call
                </Button>
              </a>
              <a href={`sms:${lead.phone.replace(/[^0-9+]/g, "")}`} className="inline-flex">
                <Button variant="outline" size="sm" className="gap-2 border-slate-200 bg-slate-50 shadow-sm hover:bg-slate-100">
                  <MessageSquare className="h-4 w-4" /> Text
                </Button>
              </a>
              <EditLeadDialog lead={lead} />
              <Button variant="destructive" size="icon" onClick={() => {
                if (confirm("Are you sure you want to delete this lead?")) {
                  deleteLead(lead.id);
                  navigate("/leads");
                }
              }}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs leading-5 text-slate-500 lg:max-w-sm lg:text-right">
              Record the next touchpoint here so this lead stays active and organized.
            </div>
          </div>
        </div>

        <div className="grid gap-3 px-5 pb-6 sm:grid-cols-2 xl:grid-cols-5 sm:px-8 lg:px-10">
          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3 shadow-sm">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Status</div>
            <div className="mt-1 font-semibold text-slate-900">{lead.status}</div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3 shadow-sm">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Asking price</div>
            <div className="mt-1 font-semibold text-slate-900">{lead.askingPrice ? `$${lead.askingPrice.toLocaleString()}` : "Not set"}</div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3 shadow-sm">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Next follow-up</div>
            <div className="mt-1 font-semibold text-slate-900">
              {nextFollowUp ? (isToday(parseISO(nextFollowUp.dueDate)) ? "Today" : format(parseISO(nextFollowUp.dueDate), "MMM d")) : "None scheduled"}
            </div>
            <div className="mt-1 text-xs text-slate-500 line-clamp-1">{nextActionCopy}</div>
            {nextFollowUp?.notes && <div className="mt-1 text-xs text-slate-400 line-clamp-1">{nextFollowUp.notes}</div>}
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3 shadow-sm">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Activity</div>
            <div className="mt-1 font-semibold text-slate-900">{activityCount} logged</div>
            <div className="mt-1 text-xs text-slate-500">Last touch {formatDistanceToNow(parseISO(lastTouchedAt), { addSuffix: true })}</div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3 shadow-sm">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Priority</div>
            <div className="mt-1 font-semibold text-slate-900">{priorityLabel}</div>
            <div className="mt-1 text-xs text-slate-500">{overdueFollowUps.length > 0 ? `${overdueFollowUps.length} overdue reminder${overdueFollowUps.length === 1 ? "" : "s"}` : "No overdue reminders"}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
        {/* Left Column - Details */}
        <div className="md:col-span-2 flex flex-col gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Lead Details</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="status">Status</Label>
                <Select value={lead.status} onValueChange={(v) => handleUpdate("status", v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="New Lead">New Lead</SelectItem>
                    <SelectItem value="Contacted">Contacted</SelectItem>
                    <SelectItem value="Follow-Up">Follow-Up</SelectItem>
                    <SelectItem value="Appointment Set">Appointment Set</SelectItem>
                    <SelectItem value="Offer Made">Offer Made</SelectItem>
                    <SelectItem value="Under Contract">Under Contract</SelectItem>
                    <SelectItem value="Dead Lead">Dead Lead</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label className="text-muted-foreground">Phone</Label>
                <div className="flex flex-wrap gap-2">
                  <span className="font-medium">{lead.phone}</span>
                  <a href={`tel:${lead.phone.replace(/[^0-9+]/g, "")}`} className="inline-flex w-full sm:w-auto">
                    <Button variant="outline" size="sm" className="h-7 px-2 w-full sm:w-auto">
                      <Phone className="w-3 h-3 mr-1" /> Call
                    </Button>
                  </a>
                  <a href={`sms:${lead.phone.replace(/[^0-9+]/g, "")}`} className="inline-flex w-full sm:w-auto">
                    <Button variant="outline" size="sm" className="h-7 px-2 w-full sm:w-auto">
                       <MessageSquare className="w-3 h-3 mr-1" /> Text
                    </Button>
                  </a>
                  <FreeSkipTraceDialog address={lead.propertyAddress} defaultOwnerName={lead.sellerName} />
                </div>
              </div>
              <div className="grid gap-2">
                <Label className="text-muted-foreground">Property Address</Label>
                <span className="font-medium">{lead.propertyAddress}</span>
              </div>
              <div className="grid gap-2">
                <Label className="text-muted-foreground">Asking Price ($)</Label>
                <span className="font-medium">{lead.askingPrice ? `$${lead.askingPrice.toLocaleString()}` : "Not Set"}</span>
              </div>
              <div className="grid gap-2 sm:col-span-2">
                <Label htmlFor="motivation">Motivation & Situation</Label>
                <Textarea 
                  id="motivation" 
                  value={lead.motivation} 
                  onChange={(e) => handleUpdate("motivation", e.target.value)} 
                  placeholder="Why are they selling? E.g., inherited, tired landlord, repairs needed..."
                  className="min-h-[80px]"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Activity className="h-5 w-5 text-slate-500" /> Activity Feed
                  </CardTitle>
                  <CardDescription>Capture every call, text, and note so the history stays useful at a glance.</CardDescription>
                </div>
                <Badge variant="outline" className="w-fit bg-slate-50">Timeline</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-4 rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                  <Textarea 
                  placeholder="Add a note with context, next steps, or objections..."
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  className="min-h-[96px] bg-white"
                />
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-wrap gap-2 text-sm">
                    <Button size="sm" variant="outline" className="flex gap-2 w-full sm:w-auto bg-white" onClick={() => handleQuickAction("Called (Spoke to seller)", "call")}>
                      <Phone className="w-4 h-4 text-blue-500" /> Log Call
                    </Button>
                    <Button size="sm" variant="outline" className="flex gap-2 w-full sm:w-auto bg-white" onClick={() => handleQuickAction("Left Voicemail", "call")}>
                      <Voicemail className="w-4 h-4 text-purple-500" /> Log Voicemail
                    </Button>
                    <Button size="sm" variant="outline" className="flex gap-2 w-full sm:w-auto bg-white" onClick={() => handleQuickAction("Texted", "text")}>
                      <MessageSquare className="w-4 h-4 text-green-500" /> Log Text
                    </Button>
                  </div>
                  <Button size="sm" onClick={handleAddNote} className="w-full sm:w-auto">Add Note</Button>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white">
                {sortedActivities.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-2 px-6 py-10 text-center">
                    <div className="rounded-full bg-slate-100 p-3 text-slate-500">
                      <Activity className="h-5 w-5" />
                    </div>
                    <p className="text-sm font-medium text-slate-700">No activity logged yet</p>
                    <p className="text-xs text-slate-500 max-w-sm">Log a call, text, or note to keep the timeline and follow-up cadence clear.</p>
                  </div>
                ) : (
                  <div className="space-y-0 relative border-l-2 border-slate-100 ml-4 py-4">
                    {sortedActivities.map((act) => (
                      <div key={act.id} className="flex gap-4 relative pb-6 pl-6 last:pb-2 pr-4">
                        <div className="absolute -left-[17px] top-0 rounded-full p-[6px] bg-white border border-slate-200 shadow-sm z-10 w-8 h-8 flex items-center justify-center">
                          {act.type === "note" && <div className="h-2 w-2 rounded-full bg-slate-400" />}
                          {act.type === "call" && <Phone className="w-3.5 h-3.5 text-blue-500" />}
                          {act.type === "text" && <MessageSquare className="w-3.5 h-3.5 text-green-500" />}
                          {act.type === "status_change" && <Sparkles className="w-3.5 h-3.5 text-amber-500" />}
                          {act.type === "meeting" && <CalendarPlus className="w-3.5 h-3.5 text-purple-500" />}
                        </div>
                        <div className="flex flex-col gap-1 flex-1 pb-1 mt-[2px] min-w-0">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="text-xs font-semibold text-slate-500 capitalize">{act.type.replace("_", " ")}</span>
                            <span className="text-xs text-slate-400">{format(parseISO(act.timestamp), "MMM d, h:mm a")}</span>
                          </div>
                          <p className="text-sm text-slate-700 font-medium break-words">{act.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm overflow-hidden">
            <CardHeader className="bg-slate-50/80 border-b pb-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-lg text-slate-800 font-bold">
                    <Zap className="h-5 w-5 text-amber-500" /> AI Assistant
                  </CardTitle>
                  <CardDescription>Turn the lead into a clear summary or a ready-to-send follow-up.</CardDescription>
                </div>
                {!useAppStore.getState().isPro && (
                  <span className="text-xs bg-slate-200 text-slate-600 px-2 py-1 rounded font-medium w-fit">PRO FEATURE</span>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="space-y-3 border-b pb-6">
                 <div className="flex items-center justify-between gap-2">
                    <h4 className="text-sm font-semibold text-slate-800">Deal Summary</h4>
                    {dealSummary && <span className="text-xs text-slate-500">Generated summary</span>}
                 </div>
                 {dealSummary ? (
                   <div className="rounded-lg border border-amber-100/70 bg-amber-50/70 p-4 text-sm text-amber-900 leading-relaxed">
                     {dealSummary}
                   </div>
                 ) : (
                   <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                     No summary yet. Generate one to get a tighter read on the opportunity.
                   </div>
                 )}
                 <Button 
                  onClick={useAppStore.getState().isPro ? handleSummarizeDeal : () => navigate("/pricing")} 
                  disabled={isSummarizing && useAppStore.getState().isPro} 
                  variant="outline"
                  className="w-full bg-slate-50 hover:bg-slate-100 h-10"
                 >
                  <ArrowUpRight className="mr-2 h-4 w-4" /> {isSummarizing ? "Summarizing..." : "Generate deal summary"}
                 </Button>
              </div>

              <div className="space-y-3">
                 <div className="flex items-center justify-between gap-2">
                    <h4 className="text-sm font-semibold text-slate-800">Follow-Up Text</h4>
                    {generatedMessage && <span className="text-xs text-slate-500">Ready to copy or send</span>}
                 </div>
              {generatedMessage ? (
                <div className="rounded-lg border border-blue-100 bg-blue-50/80 p-4 text-sm italic leading-relaxed text-blue-900">
                  “{generatedMessage}”
                  <div className="flex flex-wrap gap-2 w-full mt-3 justify-end">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-blue-600 hover:bg-blue-100/50 h-8 font-medium"
                      onClick={() => {
                        navigator.clipboard.writeText(generatedMessage);
                        toast.success("Copied to clipboard!");
                      }}
                    >
                      Copy
                    </Button>
                    <a href={`sms:${lead.phone.replace(/[^0-9+]/g, "")}?body=${encodeURIComponent(generatedMessage)}`}>
                      <Button size="sm" className="bg-blue-600 text-white hover:bg-blue-700 shadow-sm h-8">
                        <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Send
                      </Button>
                    </a>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                  Generate a personalized message once you’re ready to follow up.
                </div>
              )}
              <Button 
                onClick={useAppStore.getState().isPro ? handleGenerateMessage : () => navigate("/pricing")} 
                disabled={isGenerating && useAppStore.getState().isPro} 
                className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 transition-colors h-10 shadow-sm"
              >
                {!useAppStore.getState().isPro ? "Choose a plan" : isGenerating ? "Generating..." : "Generate text message"}
              </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Follow Ups & Nearby Deals */}
        <div className="flex flex-col gap-6">

          <Card className="border-emerald-200 shadow-sm bg-emerald-50/50">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-emerald-800 flex items-center gap-2 text-lg">
                    <Building className="w-5 h-5 text-emerald-600" /> Nearby Deals
                  </CardTitle>
                  <CardDescription className="text-emerald-800/70">Scan adjacent opportunities and compare this lead against nearby deals.</CardDescription>
                </div>
                <Button variant="ghost" size="sm" className="text-emerald-700 hover:bg-emerald-100/60" onClick={() => navigate('/map')}>
                  <MapPin className="mr-2 h-4 w-4" /> Open map view
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {opportunities.length === 0 ? (
                <div className="text-sm text-emerald-700/70 p-5 bg-white rounded-xl border border-emerald-100 flex flex-col items-center justify-center text-center">
                   <MapPin className="w-6 h-6 text-emerald-300 mb-2" />
                   <div className="font-medium text-emerald-900">No nearby properties yet</div>
                   <div className="mt-1 max-w-sm">Import deals or use the map to uncover stronger nearby opportunities.</div>
                   <div className="mt-3 flex gap-2">
                     <Link to="/map" className="text-emerald-600 underline font-medium text-xs">Open map</Link>
                     <span className="text-emerald-300">•</span>
                     <Link to="/opportunities" className="text-emerald-600 underline font-medium text-xs">Browse deals</Link>
                   </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {opportunities.slice(0, 3).map(opp => (
                     <div key={opp.id} className="bg-white p-3 border border-emerald-100 rounded-lg hover:border-emerald-300 transition-colors cursor-pointer" onClick={() => navigate('/opportunities')}>
                        <div className="flex justify-between items-start mb-1 gap-2">
                          <span className="font-semibold text-sm text-slate-800 truncate pr-2">{opp.propertyAddress}</span>
                          <Badge className="bg-emerald-100 text-emerald-700 shrink-0 text-[10px] px-1.5 py-0">Score {opp.opportunityScore}</Badge>
                        </div>
                        {opp.tags && opp.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                             {opp.tags.slice(0, 2).map((t, idx) => (
                               <span key={idx} className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{t}</span>
                             ))}
                             {opp.tags.length > 2 && <span className="text-[10px] text-slate-400">+{opp.tags.length - 2}</span>}
                          </div>
                        )}
                        <div className="mt-2 text-[11px] font-medium text-emerald-700 inline-flex items-center gap-1">
                          View opportunity <ArrowUpRight className="h-3 w-3" />
                        </div>
                     </div>
                  ))}
                  <Button variant="link" className="w-full text-emerald-600 h-8" onClick={() => {
                     if (lead.lat && lead.lng) {
                       navigate(`/map?lat=${lead.lat}&lng=${lead.lng}&radius=5`);
                     } else {
                       navigate('/map');
                     }
                  }}>
                    View Deals on Map
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Schedule Follow-Up</CardTitle>
              <CardDescription>Pick the next touchpoint and add a short goal so the reminder is actionable.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2">
                <Label>Reminder window</Label>
                <Select value={followUpDays} onValueChange={setFollowUpDays}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Tomorrow</SelectItem>
                    <SelectItem value="2">In 2 days</SelectItem>
                    <SelectItem value="7">Next week</SelectItem>
                    <SelectItem value="14">In 2 weeks</SelectItem>
                    <SelectItem value="30">In 30 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Notes / Goal</Label>
                <Input 
                  placeholder="E.g., See if they spoke to spouse" 
                  value={followUpNotes}
                  onChange={(e) => setFollowUpNotes(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">This will appear in pending follow-ups and the activity trail.</p>
              </div>
              <Button className="w-full" onClick={handleAddFollowUp}>
                <CalendarPlus className="mr-2 h-4 w-4" /> Add follow-up
              </Button>
            </CardContent>
          </Card>

          <DocumentManager 
            entityId={lead.id} 
            entityType="lead" 
            title="Lead Files" 
            description="Contracts, photos, and addendums in one place." 
          />

          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center justify-between gap-2">
                <span>Pending Follow-Ups</span>
                <Badge variant="outline" className="bg-slate-50">{pendingFollowUps.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pendingFollowUps.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                  No pending follow-ups. Add a reminder above to keep momentum on the lead.
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingFollowUps.map(fu => {
                    const date = parseISO(fu.dueDate);
                    const overdue = isPast(date) && !isToday(date);
                    return (
                      <div key={fu.id} className="flex flex-col gap-2 p-3 bg-muted/50 rounded-md border text-sm">
                        <div className="flex items-start justify-between">
                          <span className={`font-medium ${overdue ? 'text-destructive' : ''}`}>
                            {isToday(date) ? "Today" : format(date, "MMM d")}
                            {overdue && " (Overdue)"}
                          </span>
                          <Button variant="ghost" size="icon" className="h-6 w-6 -mt-1 -mr-1 text-muted-foreground hover:text-primary" onClick={() => completeFollowUp(lead.id, fu.id)}>
                            <CheckCircle2 className="h-4 w-4" />
                          </Button>
                        </div>
                        {fu.notes && <span className="text-muted-foreground">{fu.notes}</span>}
                      </div>
                    )
                  })}
                </div>
              )}
            </CardContent>
          </Card>

           {completedFollowUps.length > 0 && (
             <Card className="border-slate-200 shadow-sm">
             <CardHeader className="pb-3">
               <CardTitle className="text-sm text-muted-foreground flex items-center justify-between gap-2">
                 <span>Completed Follow-Ups</span>
                 <Badge variant="outline" className="bg-slate-50">{completedFollowUps.length}</Badge>
               </CardTitle>
              </CardHeader>
              <CardContent>
                 <div className="space-y-2 opacity-60">
                  {completedFollowUps.slice(0, 5).map(fu => (
                     <div key={fu.id} className="flex justify-between items-center text-xs border-b pb-2 last:border-0">
                       <span className="line-through">{format(parseISO(fu.dueDate), "MMM d, yyyy")}</span>
                       <span className="truncate max-w-[120px] text-right">{fu.notes || "Completed reminder"}</span>
                     </div>
                  ))}
                  </div>
              </CardContent>
            </Card>
           )}
        </div>
      </div>
    </div>
  );
}
